import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Sparkles, ArrowRight } from "lucide-react";

const WEBSITE_TIERS = [
  {
    name: "Starter Website",
    tagline: "High-Converting Design",
    price: "$1,000",
    period: "one-time",
    description:
      "Great for small businesses getting started online with a polished web presence.",
    features: [
      "Modern, 5-page website",
      "Light SEO included",
      "Clean, professional layout",
      "Mobile-friendly responsive build",
      "Contact form & call-to-action setup",
    ],
    popular: false,
  },
  {
    name: "Custom Website",
    tagline: "Built to Rank & Convert",
    price: "$3,000",
    period: "one-time",
    description:
      "A custom-engineered website built to help your business rank, convert and scale.",
    features: [
      "Up to 10 pages",
      "High-level SEO included",
      "Professionally written structure & content",
      "Premium mobile-first design",
      "Schema markup + technical SEO foundation",
      "Built to rank and convert from day one",
    ],
    popular: true,
  },
];

const SEO_TIERS = [
  {
    name: "Starter",
    tagline: "Get Found First",
    price: "$997",
    period: "/ month",
    description:
      "For contractors getting found online for the first time.",
    features: [
      "Google Business Profile setup (basic)",
      "On-page SEO basics",
      "Local SEO setup",
      "2 SEO blog posts / month",
      "15 keywords tracked",
      "Monthly performance reporting",
      "Click-to-call & quote request form",
      "12-month agreement",
    ],
    popular: false,
  },
  {
    name: "Growth",
    tagline: "Most Popular",
    price: "$1,750",
    period: "/ month",
    description:
      "The tier where most home-service businesses see their first real breakthrough.",
    features: [
      "Full SEO foundation overhaul (Month 1)",
      "Complete Google Business Profile optimization",
      "Local SEO & citation building (40+ authority directories, NAP cleanup)",
      "4 SEO blog posts / month",
      "40+ keywords tracked with monthly ranking reports",
      "Service-area expansion content",
      "Competitor ranking analysis",
      "Monthly strategy calls",
      "12-month agreement",
    ],
    popular: true,
  },
  {
    name: "Dominator",
    tagline: "Own Your Market",
    price: "$2,997",
    period: "/ month",
    description:
      "For contractors ready to dominate their entire local market.",
    features: [
      "Everything in Growth — plus:",
      "Aggressive local SEO campaign",
      "Competitor gap analysis (pages, keywords & backlinks)",
      "6 SEO blog posts / month",
      "75+ keywords tracked",
      "Full Google Business Profile management",
      "Reputation management & review workflows",
      "Weekly reporting (instead of monthly)",
    ],
    popular: false,
  },
];

const TABS = [
  { id: "website", label: "Website Development", sub: "One-time projects" },
  { id: "seo", label: "Monthly SEO", sub: "Recurring growth" },
];

export default function Pricing() {
  const [tab, setTab] = useState("website");
  const tiers = tab === "website" ? WEBSITE_TIERS : SEO_TIERS;
  const cols = tiers.length === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3";

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="section relative overflow-hidden"
    >
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] radial-glow opacity-30 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
            Pricing
          </div>
          <h2 className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl mx-auto leading-[1.05]">
            Transparent pricing.{" "}
            <span className="text-gradient-brand">Premium outcomes.</span>
          </h2>
          <p className="mt-5 text-white/55 max-w-2xl mx-auto">
            Flat-rate websites and monthly SEO packages — no surprises, no
            inflated retainers. Pick the path that matches your stage of
            growth.
          </p>
        </div>

        {/* Tab toggle */}
        <div className="flex justify-center mb-12">
          <div
            data-testid="pricing-tabs"
            className="inline-flex p-1 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-md"
          >
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`pricing-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`relative px-5 sm:px-7 py-3 rounded-full text-sm font-semibold transition-colors duration-300 ${
                    active ? "text-black" : "text-white/65 hover:text-white"
                  }`}
                >
                  {active && (
                    <motion.span
                      layoutId="pricing-tab-active"
                      transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                      className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600 to-cyan-400 shadow-[0_8px_30px_rgba(0,229,255,0.35)]"
                    />
                  )}
                  <span className="relative flex flex-col items-start sm:items-center sm:flex-row sm:gap-2">
                    {t.label}
                    <span
                      className={`text-[10px] uppercase tracking-widest font-medium ${
                        active ? "text-black/70" : "text-white/35"
                      }`}
                    >
                      {t.sub}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className={`grid grid-cols-1 md:grid-cols-2 ${cols} gap-5 lg:gap-6 max-w-5xl mx-auto lg:max-w-none`}
          >
            {tiers.map((p, i) => (
              <PricingCard key={p.name} plan={p} index={i} tabId={tab} />
            ))}
          </motion.div>
        </AnimatePresence>

        <p className="mt-12 text-center text-sm text-white/40">
          Custom enterprise plans available. Need something different?{" "}
          <a
            href="#audit"
            data-testid="pricing-custom-link"
            className="text-cyan-400 hover:text-cyan-300 font-semibold"
          >
            Get a custom quote →
          </a>
        </p>
      </div>
    </section>
  );
}

function PricingCard({ plan, index, tabId }) {
  const isPopular = plan.popular;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      data-testid={`pricing-card-${tabId}-${index}`}
      className={`relative rounded-3xl p-[1px] ${
        isPopular
          ? "bg-gradient-to-br from-blue-600 via-cyan-400 to-blue-600"
          : "bg-white/10"
      }`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-blue-600 to-cyan-400 text-black text-[10px] font-bold tracking-widest uppercase shadow-[0_8px_30px_rgba(0,229,255,0.45)]">
            <Sparkles className="w-3 h-3" />
            Most Popular
          </div>
        </div>
      )}

      <div
        className={`relative rounded-[calc(1.5rem-1px)] p-8 h-full flex flex-col ${
          isPopular ? "bg-[#0a0a0a]" : "bg-[#0a0a0a]"
        }`}
      >
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
          {plan.tagline}
        </div>
        <h3 className="mt-2 font-display text-2xl md:text-3xl font-bold">
          {plan.name}
        </h3>

        <div className="mt-5 flex items-baseline gap-1.5">
          <span className="font-display text-5xl font-extrabold text-white">
            {plan.price}
          </span>
          <span className="text-white/45 text-sm">{plan.period}</span>
        </div>

        <p className="mt-3 text-white/55 text-sm leading-relaxed min-h-[3rem]">
          {plan.description}
        </p>

        <div className="my-7 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        <ul className="space-y-3 flex-1">
          {plan.features.map((f) => (
            <li key={f} className="flex items-start gap-3 text-sm">
              <span
                className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                  isPopular
                    ? "bg-gradient-to-br from-blue-600 to-cyan-400"
                    : "bg-white/5 border border-white/10"
                }`}
              >
                <Check
                  className={`w-3 h-3 ${
                    isPopular ? "text-black" : "text-cyan-400"
                  }`}
                />
              </span>
              <span className="text-white/80 leading-relaxed">{f}</span>
            </li>
          ))}
        </ul>

        <a
          href="#audit"
          data-testid={`pricing-cta-${tabId}-${index}`}
          className={`group mt-8 inline-flex items-center justify-center gap-2 w-full py-3.5 rounded-full font-semibold transition-all duration-300 ${
            isPopular
              ? "bg-gradient-to-r from-blue-600 to-cyan-400 text-black hover:shadow-[0_0_40px_rgba(0,229,255,0.5)]"
              : "bg-white/5 border border-white/10 text-white hover:bg-white/10 hover:border-cyan-500/30"
          }`}
        >
          Get Started
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </a>
      </div>
    </motion.div>
  );
}
